import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NumberFlow from '@number-flow/react';
import { Users, Building2, Swords, Grid3x3, Target } from 'lucide-react';

interface StatItemProps {
  value: number;
  suffix?: string;
  label: string;
  icon: React.ElementType;
  delay?: number;
  showLiveIndicator?: boolean;
  isLive?: boolean;
  lang?: 'en' | 'fr' | 'de' | 'it';
}

const StatItem: React.FC<StatItemProps> = ({ value, suffix = '', label, icon: Icon, delay = 0, showLiveIndicator = false, isLive = false, lang = 'en' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [liveValue, setLiveValue] = useState(value);

  // Initial animation from 0 to target value
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDisplayValue(value);
    }, delay * 1000);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  // Live incrementing for live counters (matches/sets/points)
  useEffect(() => {
    if (!isLive) return;

    // Calculate realistic increment rate based on yearly target
    const ITEMS_PER_YEAR = value; // Use the value prop to determine what we're counting
    const START_DATE = new Date('2025-01-01T00:00:00');

    // Items per millisecond
    const ITEMS_PER_MS = ITEMS_PER_YEAR / (365.25 * 24 * 60 * 60 * 1000);

    // Calculate how many milliseconds between increments (realistic timing)
    // If we have X items per ms, then 1 item takes 1/X ms
    const MS_PER_INCREMENT = Math.floor(1 / ITEMS_PER_MS);

    // Calculate base count up to now
    const now = new Date();
    const elapsed = now.getTime() - START_DATE.getTime();
    const baseCount = Math.floor(ITEMS_PER_MS * elapsed);

    let interval: NodeJS.Timeout | null = null;
    let liveTimeout: NodeJS.Timeout | null = null;

    // Animate to base value first (using the same animation timing as other stats)
    const animationTimeout = setTimeout(() => {
      setDisplayValue(baseCount);
      setLiveValue(baseCount);

      // After animation completes, start live incrementing at realistic rate
      liveTimeout = setTimeout(() => {
        interval = setInterval(() => {
          setLiveValue(prev => prev + 1);
        }, MS_PER_INCREMENT);
      }, 2000); // Wait for NumberFlow animation to complete
    }, delay * 1000);

    return () => {
      clearTimeout(animationTimeout);
      if (liveTimeout) clearTimeout(liveTimeout);
      if (interval) clearInterval(interval);
    };
  }, [isLive, delay, value, label]);

  const finalValue = isLive ? liveValue : displayValue;

  // Map language to appropriate locale for number formatting
  const localeMap: Record<string, string> = {
    en: 'de-CH', // Swiss format with apostrophes
    fr: 'fr-CH', // French Swiss format with spaces
    de: 'de-CH', // German Swiss format with apostrophes
    it: 'it-CH'  // Italian Swiss format
  };

  return (
    <motion.div
      className="flex items-center gap-1.5 px-2 py-1 transition-colors duration-300"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: delay }}
    >
      <Icon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 transition-colors duration-300" />
      <div className="flex items-baseline gap-1">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 transition-colors duration-300">
          <NumberFlow
            value={finalValue}
            locales={localeMap[lang] || 'de-CH'}
          />
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{label}</div>
      </div>
    </motion.div>
  );
};

interface AnimatedStatsProps {
  lang?: 'en' | 'fr' | 'de' | 'it';
}

const AnimatedStats: React.FC<AnimatedStatsProps> = ({ lang = 'en' }) => {
  const translations = {
    en: {
      players: 'Players',
      matches: 'Matches',
      sets: 'Sets',
      points: 'Points',
      clubs: 'Clubs',
      liveData: 'Live Data',
      tracking: 'Tracking Swiss table tennis'
    },
    fr: {
      players: 'Joueurs',
      matches: 'Matchs',
      sets: 'Sets',
      points: 'Points',
      clubs: 'Clubs',
      liveData: 'Données en direct',
      tracking: 'Suivi du tennis de table suisse'
    },
    de: {
      players: 'Spieler',
      matches: 'Spiele',
      sets: 'Sätze',
      points: 'Punkte',
      clubs: 'Vereine',
      liveData: 'Live-Daten',
      tracking: 'Schweizer Tischtennis verfolgen'
    },
    it: {
      players: 'Giocatori',
      matches: 'Partite',
      sets: 'Set',
      points: 'Punti',
      clubs: 'Club',
      liveData: 'Dati in tempo reale',
      tracking: 'Tracciamento del ping pong svizzero'
    }
  };

  const t = translations[lang];

  return (
    <motion.div
      className="flex items-center justify-center gap-1 flex-wrap mb-8 opacity-70"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 0.7 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 1.9 }}
    >
      <StatItem
        value={249}
        label={t.clubs}
        icon={Building2}
        delay={2.0}
        lang={lang}
      />
      <span className="text-gray-400 dark:text-gray-600 text-xs transition-colors duration-300">•</span>
      <StatItem
        value={5535}
        label={t.players}
        icon={Users}
        delay={2.15}
        lang={lang}
      />
      <span className="text-gray-400 dark:text-gray-600 text-xs transition-colors duration-300">•</span>
      <StatItem
        value={75000}
        label={t.matches}
        icon={Swords}
        delay={2.3}
        isLive={true}
        lang={lang}
      />
      <span className="text-gray-400 dark:text-gray-600 text-xs transition-colors duration-300">•</span>
      <StatItem
        value={300000}
        label={t.sets}
        icon={Grid3x3}
        delay={2.45}
        isLive={true}
        lang={lang}
      />
      <span className="text-gray-400 dark:text-gray-600 text-xs transition-colors duration-300">•</span>
      <StatItem
        value={6000000}
        label={t.points}
        icon={Target}
        delay={2.6}
        isLive={true}
        lang={lang}
      />
    </motion.div>
  );
};

export default AnimatedStats;
